import React, { useState } from 'react';
import { direction_type } from '../../types';
import { uiToApi } from './statusData';
import { initialValues } from '../..';
import { FormError } from 'components';
import { filterStr, filterStringReducer, isShowButton } from '../utils';
import dayjs from 'dayjs';
import { Filter16, Close16 } from '@carbon/icons-react';
import { isBrowser } from 'react-device-detect';

import {
  DatePicker,
  DatePickerInput,
  Button,
  Form,
  TextInput,
  TimePicker,
  TimePickerSelect,
  SelectItem,
  MultiSelect
} from 'carbon-components-react';
import { useFormik } from 'formik';
import { SearchEmailsSchema } from 'utilities/validation';

const ApplyFilterForm = ({
  handleFilter,
  direction,
  filterValues,
  setFilterValues,
  handleOpenCloseFilter,
  pageWrapperElem
}: any) => {
  const [statusOptions, setStatusOptions] = useState<undefined | Array<string>>(
    filterValues.status
  );
  const {
    touched,
    errors,
    values,
    handleSubmit,
    handleChange,
    handleBlur,
    resetForm
  } = useFormik({
    enableReinitialize: true,
    initialValues: filterValues,
    validationSchema: SearchEmailsSchema,
    onSubmit: (values: any) => {
      const formData = Object.entries({
        ...values,
        status: statusOptions
      }).reduce(filterStringReducer, {});
      const filteredString = filterStr({ formData, direction });
      handleFilter(filteredString);
      setFilterValues({ ...values, status: statusOptions });
    }
  });
  const handleResetFilter = () => {
    resetForm();
    setFilterValues(initialValues);
    const formData = Object.entries({
      ...initialValues
    }).reduce(filterStringReducer, {});
    const filteredString = filterStr({ formData, direction });
    handleFilter(filteredString);
  };

  const onCalendarClose = (val: any, strValue: any, self: any) => {
    strValue && self.setDate(strValue, true, 'd/m/Y');
  };

  return (
    <div className="search-emails-filter">
      <div
        className={`search-emails-filter-button-form ${
          pageWrapperElem &&
          pageWrapperElem.scrollHeight > pageWrapperElem.clientHeight &&
          isBrowser
            ? 'parent-overflow'
            : ''
        }`}
        onClick={() => handleOpenCloseFilter(false)}
      >
        <Filter16 />
      </div>
      <section
        className={`search-emails-filter-main ${
          pageWrapperElem &&
          pageWrapperElem.scrollHeight > pageWrapperElem.clientHeight &&
          isBrowser
            ? 'parent-overflow'
            : ''
        }`}
      >
        <Form className="apply-filter-form-wrapper" onSubmit={handleSubmit}>
          <div className="heading">
            <p>Filter the view</p>
            <Button
              kind="ghost"
              size="sm"
              onClick={() => handleOpenCloseFilter(false)}
            >
              <Close16 />
            </Button>
          </div>
          <div className="sub-heading">
            {direction === direction_type.OUTBOUND ? 'Sent' : 'Received'}
          </div>
          <DatePicker
            maxDate={dayjs().format('DD/MM/YYYY')}
            className="apply-filter-form-datepicker-from"
            dateFormat="d/m/Y"
            id="date-picker-from"
            light={false}
            datePickerType="single"
            onClose={onCalendarClose}
          >
            <DatePickerInput
              id="dateFrom"
              labelText="From"
              onChange={handleChange}
              onBlur={handleChange}
              placeholder="dd/mm/yyyy"
              value={values.dateFrom}
            />
          </DatePicker>
          {touched && errors.dateFrom && (
            <FormError>{errors.dateFrom}</FormError>
          )}

          {values.dateFrom && (
            <TimePicker
              id="hourFrom"
              pattern="(1[012]|[1-9]):[0-5][0-9](\\s)?"
              placeholder="h:mm"
              className="apply-filter-form-time-picker-from"
              onChange={handleChange}
              onBlur={handleChange}
              disabled={!values.dateFrom}
              value={values.hourFrom || '12:00'}
            >
              <TimePickerSelect
                id="amPmFrom"
                labelText="time-format-picker-from"
                disabled={!values.dateFrom && !values.hourFrom}
                value={values.amPmFrom}
                onChange={handleChange}
                onBlur={handleChange}
              >
                <SelectItem value="AM" text="AM" />
                <SelectItem value="PM" text="PM" />
              </TimePickerSelect>
            </TimePicker>
          )}
          {touched && errors.hourFrom && (
            <FormError>{errors.hourFrom}</FormError>
          )}

          <DatePicker
            className="apply-filter-form-datepicker-to"
            maxDate={dayjs().format('DD/MM/YYYY')}
            dateFormat="d/m/Y"
            id="date-picker-to"
            light={false}
            datePickerType="single"
            onClose={onCalendarClose}
          >
            <DatePickerInput
              id="dateTo"
              labelText="To"
              placeholder="dd/mm/yyyy"
              value={values.dateTo}
              onChange={handleChange}
              onBlur={handleChange}
            />
          </DatePicker>
          {touched && errors.dateTo && <FormError>{errors.dateTo}</FormError>}

          {values.dateTo && (
            <TimePicker
              id="hourTo"
              pattern="(1[012]|[1-9]):[0-5][0-9](\\s)?"
              placeholder="h:mm"
              className="apply-filter-form-time-picker-to"
              value={values.hourTo || '12:00'}
              disabled={!values.dateTo}
              onChange={handleChange}
              onBlur={handleChange}
            >
              <TimePickerSelect
                id="amPmTo"
                labelText="time-format-picker-to"
                disabled={!values.dateTo && !values.hourTo}
                value={values.amPmTo}
                onChange={handleChange}
                onBlur={handleChange}
              >
                <SelectItem value="AM" text="AM" />
                <SelectItem value="PM" text="PM" />
              </TimePickerSelect>
            </TimePicker>
          )}
          {touched && errors.hourTo && <FormError>{errors.hourTo}</FormError>}
          <hr className="divider" />
          {direction === direction_type.OUTBOUND && (
            <div>
              <MultiSelect
                items={Object.keys(uiToApi)}
                id="apply-filter-status"
                itemToString={(item) => (item ? item : '')}
                onChange={({ selectedItems }) =>
                  setStatusOptions(selectedItems)
                }
                initialSelectedItems={statusOptions}
                label="Choose an option"
                titleText="Status"
              />
              <hr className="divider" />
            </div>
          )}

          <TextInput
            id="recipientContains"
            name="recipientContains"
            labelText={
              direction === direction_type.OUTBOUND
                ? 'Recipient contains'
                : 'Sender contains'
            }
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.recipientContains}
            data-testid="recipientContains"
          />
          {touched && errors.recipientContains && (
            <FormError>{errors.recipientContains}</FormError>
          )}
          <TextInput
            id="subjectContains"
            name="subjectContains"
            labelText="Subject contains"
            onBlur={handleBlur}
            onChange={handleChange}
            value={values.subjectContains}
            data-testid="subjectContains"
          />
          {touched && errors.subjectContains && (
            <FormError>{errors.subjectContains}</FormError>
          )}
          <div className="apply-form-button-container">
            <Button kind="ghost" onClick={handleResetFilter}>
              Reset Filters
            </Button>
            <Button
              type="submit"
              disabled={!isShowButton(values, errors, statusOptions)}
            >
              Apply
            </Button>
          </div>
        </Form>
      </section>
    </div>
  );
};
export default ApplyFilterForm;
